import { Icon } from "../kit";
import { ZERO_INSET, type CardInset } from "./layout";

/**
 * Where the studio is in the fetch → addModelUrl → apply-config sequence.
 *
 * `idle` covers both "no model to load" (the /studio scratch route) and the
 * moment before the loader starts, so the empty state still shows there.
 */
export type ModelLoadStatus =
  | { phase: "idle" }
  | { phase: "resolving" }
  | { phase: "loading"; name?: string }
  | { phase: "applying"; name?: string }
  | { phase: "done" }
  | { phase: "error"; message: string };

/** True while the studio is fetching or mounting a model. */
export function isLoadingModel(s: ModelLoadStatus): boolean {
  return s.phase === "resolving" || s.phase === "loading" || s.phase === "applying";
}

const COPY: Record<string, string> = {
  resolving: "Preparing model…",
  loading: "Loading model…",
  applying: "Applying saved scene…",
};

/**
 * Canvas overlay for the model-load window.
 *
 * The studio mounts its full chrome the instant the iframe handshake completes,
 * but the model itself takes a fetch + a signed-URL round-trip + a GLB parse
 * after that. That gap was previously filled by the "Drag and drop a 3D file
 * here" empty state — actively telling the user nothing was loading while a
 * model was in fact on its way — and a failed load never cleared it, so a
 * broken model looked exactly like an empty scene.
 */
export function ModelLoadOverlay({
  status,
  onRetry,
  inset,
}: {
  status: ModelLoadStatus;
  onRetry?: () => void;
  /** Box of the canvas card, so the overlay centres on the MODEL, not the row. */
  inset?: CardInset;
}) {
  if (status.phase === "error") {
    return (
      <div className="pointer-events-auto absolute z-[6] flex items-center justify-center" style={inset ?? ZERO_INSET}>
        <div className="flex w-[400px] max-w-[80%] flex-col items-center gap-3 rounded-2xl border border-ed-border bg-ed-panel/90 px-8 py-9 text-center backdrop-blur-sm">
          <Icon name="error" size={32} className="text-ed-red" />
          <p className="text-[14px] font-semibold text-ed-text">Couldn’t load this model</p>
          <p className="text-[12px] leading-relaxed text-ed-text-2">{status.message}</p>
          {onRetry ? (
            <button
              onClick={onRetry}
              className="mt-1 rounded border border-ed-accent bg-ed-accent px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-ed-blue"
            >
              Try again
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (!isLoadingModel(status)) return null;
  const name = "name" in status ? status.name : undefined;

  return (
    // pointer-events-none: orbiting the (empty) scene stays possible, and this
    // never swallows a click meant for the rails behind it.
    <div className="pointer-events-none absolute z-[6] flex items-center justify-center" style={inset ?? ZERO_INSET}>
      <div className="flex flex-col items-center gap-3 rounded-2xl border border-ed-border bg-ed-panel/85 px-8 py-7 text-center backdrop-blur-sm">
        <Icon name="progress_activity" size={28} className="animate-spin text-ed-accent" />
        <div>
          <p className="text-[13px] font-semibold text-ed-text">{COPY[status.phase]}</p>
          {name ? <p className="mt-0.5 text-[12px] text-ed-text-3">{name}</p> : null}
        </div>
      </div>
    </div>
  );
}
